import * as styles from "./SectionDogList.module.scss";

import React, { useMemo } from "react";

import { Pagination } from "../../../components/Pagination/Pagination";
import DogListingCard from "../../../components/SignPostCards/DogListingCard";
import PromotionCardToDisplay from "../../../components/SignPostCards/PromotionCards/PromotionCardToDisplay";
import { SponsorDogPromoCardSquare } from "../../../components/SignPostCards/SponsorDogPromoCard/Square/SponsorDogPromoCardSquare";
import LoadingIndicator from "../../../components/common/LoadingIndicator/LoadingIndicator";
import { useStaticData } from "../../../hooks/useStaticData";
import { shuffleArray } from "../../../utils/array";
import { mapEsDogDocToSignpostCard } from "../../../utils/mappers";

const PROMOS_PER_PAGE = 3;
const DOGS_PER_PROMO = 5;
export interface DogListProps {
	dogData: DogsTrust.DogData[];
	totalResults: number;
	onFirstPage: () => void;
	onLastPage: () => void;
	onPreviousPage: () => void;
	onNextPage: () => void;
	userLocation?: DogsTrust.Place;
	queryStrings: string;
	dogApiLoading: boolean;
	pagination?: DogsTrust.PaginationProps;
	pageCount?: number;
	currentPage?: number;
}
export const SectionDogList: React.FC<DogListProps> = ({
	dogData = [],
	userLocation,
	dogApiLoading,
	queryStrings,
	pagination,
	onFirstPage,
	onLastPage,
	onPreviousPage,
	onNextPage,
	pageCount,
	currentPage,
}) => {
	const { dogListingPromos, sponsorDogs } = useStaticData([
		"dogListingPromos",
		"sponsorDogs",
	]);

	// The dogs mapped to signpost cards
	const dogsToRender = useMemo(
		() =>
			dogData.map((dog) =>
				mapEsDogDocToSignpostCard(dog, userLocation, queryStrings),
			),
		[dogData, userLocation, queryStrings],
	);

	// The promos or sponsor dogs mapped to signpost cards
	const promosToRender: (
		| DogsTrust.PromotionCardToDisplayProps
		| DogsTrust.SponsorDogPromoCardSquareProps
	)[] = useMemo(
		() => [...shuffleArray(dogListingPromos), ...(sponsorDogs || [])],
		[dogListingPromos, sponsorDogs],
	);

	const promoStartIndex = currentPage * PROMOS_PER_PAGE;

	// list of dogs with promos
	const dogsListWithPromos = useMemo(() => {
		const combinedList: React.ReactNode[] = [];
		let promoIndex = promoStartIndex;

		for (let i = 0; i < dogsToRender.length; i++) {
			combinedList.push(
				<DogListingCard
					key={dogsToRender[i].id}
					{...dogsToRender[i]}
					pagination={pagination}
					linkId={`dog-card-${dogsToRender[i].id}`}
				/>,
			);

			if (
				(i + 1) % DOGS_PER_PROMO === 0 &&
				promoIndex < promosToRender.length
			) {
				const promo = promosToRender[promoIndex];
				if (promo.type !== "PromoCard") {
					combinedList.push(
						<SponsorDogPromoCardSquare
							key={`sponsor-${promoIndex}`}
							{...promo}
							isOnDogListing={true}
						/>,
					);
				} else if (promo.promotionType !== "PromoCardNav") {
					combinedList.push(
						<PromotionCardToDisplay
							key={`promo-${promoIndex}`}
							{...promo}
							isOnDogListing={true}
						/>,
					);
				}
				promoIndex++;
			}
		}

		return combinedList;
	}, [dogsToRender, promosToRender, pagination, promoStartIndex]);

	return (
		<>
			<div className={styles.component}>{dogsListWithPromos}</div>
			<div className={styles.viewmorewrapper}>
				{dogApiLoading ? (
					<div
						className={styles.loadingoverlay}
						style={{ display: dogApiLoading ? "flex" : "none" }}
					>
						<div className={styles.loadingicon}>
							<LoadingIndicator loading={dogApiLoading} scale={1} />
						</div>
					</div>
				) : (
					<Pagination
						onFirstClick={onFirstPage}
						onPreviousClick={onPreviousPage}
						onNextClick={onNextPage}
						onLastClick={onLastPage}
						firstDisabled={currentPage === 0}
						lastDisabled={currentPage === pageCount - 1}
						previousDisabled={currentPage === 0}
						nextDisabled={currentPage === pageCount - 1}
						total={pageCount}
						position={currentPage}
						noOffset={true}
						isOnListingPage={true}
					/>
				)}
			</div>
		</>
	);
};

export default SectionDogList;
